import {
  BillingPreferenceResponse,
  BillingPreferenceUpdateSchema,
  type ClientByIdResponse,
} from '@/types/client.types';
import { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import axiosInstance from './axiosInstance';
import { normalizeAxiosError } from '@/lib/error';

type ClientApiResult<T> = { data?: T; error?: GenericErrorResponse };

export const getClientById = async (clientId: string): Promise<ClientApiResult<ClientByIdResponse>> => {
  try {
    const response = await axiosInstance.get<ClientByIdResponse>(`/clients/${clientId}`);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useClientById = (
  clientId: string | undefined,
  options?: { refreshKey?: number; enabled?: boolean }
) =>
  useQuery<ClientByIdResponse>({
    queryKey: ['client', clientId, options?.refreshKey],
    queryFn: async () => {
      const { data, error } = await getClientById(clientId!);
      if (error) throw error;
      return data!;
    },
    enabled: (options?.enabled ?? true) && !!clientId,
    staleTime: 1000 * 60 * 5,
  });

export const getContactsCsv = async (
  clientId: string
): Promise<{
  data?: string;
  error?: GenericErrorResponse;
}> => {
  const path = `clients/${clientId}/contacts/csv`;
  try {
    const response = await axios.post<string>(
      path,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const useClientBillingPreferenceGet = (clientId: string | undefined) => {
  return useQuery<AxiosResponse<BillingPreferenceResponse>, GenericErrorResponse>({
    queryKey: ['client', 'billing-preferences', clientId],
    queryFn: async () => {
      if (!clientId) throw new Error('Client ID is required');
      const response = await axios.get<BillingPreferenceResponse>(`/clients/${clientId}/billing-preferences`);
      return response;
    },
    enabled: !!clientId,
  });
};

export const useClientBillingPreferenceUpdate = () => {
  return useMutation<
    BillingPreferenceResponse,
    AxiosError<GenericErrorResponse>,
    { clientId: string; data: BillingPreferenceUpdateSchema }
  >({
    mutationFn: async ({ clientId, data }) => {
      const response = await axios.put<BillingPreferenceResponse>(`/clients/${clientId}/billing-preferences`, {
        ...data,
      });
      return response.data;
    },
  });
};
